import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Loader2, ImagePlus, Calendar, StickyNote, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatDateBR, todayISO } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Filters } from "../components/Filters";
import { VoiceInput } from "../components/VoiceInput";
import { AuthImage } from "../components/AuthImage";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const emptyForm = { date: todayISO(), description: "", observations: "", photos: [] };

export default function Reports() {
    const { user } = useAuth();
    const qc = useQueryClient();
    const isColaborador = user?.role === "colaborador";
    const canEdit = isColaborador || user?.role === "gestor" || user?.is_master;
    const [filters, setFilters] = useState({ q: "", start: "", end: "" });
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    const openNew = () => { setEditId(null); setForm(emptyForm); setOpen(true); };
    const openEdit = (r) => {
        setEditId(r.id);
        setForm({ date: (r.date || "").slice(0, 10) || todayISO(), description: r.description || "", observations: r.observations || "", photos: r.photos || [] });
        setOpen(true);
    };
    const onOpenChange = (o) => { setOpen(o); if (!o) { setEditId(null); setForm(emptyForm); } };

    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.start) params.set("start", filters.start);
    if (filters.end) params.set("end", filters.end);

    const { data: reports = [] } = useQuery({ queryKey: ["reports", filters], queryFn: async () => (await api.get(`/reports?${params}`)).data });

    const onVoiceResult = (fields) => {
        setForm((f) => ({
            ...f,
            description: fields.description ?? f.description,
            observations: fields.observations ?? f.observations,
        }));
    };

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const paths = [];
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                paths.push(data.path);
            }
            setForm((f) => ({ ...f, photos: [...f.photos, ...paths] }));
            toast.success("Foto(s) anexada(s)");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    const removePhoto = (p) => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== p) }));

    const save = async () => {
        if (!form.description) { toast.error("Descreva a rotina do dia"); return; }
        setSaving(true);
        try {
            if (editId) {
                await api.put(`/reports/${editId}`, form);
            } else {
                await api.post("/reports", form);
            }
            qc.invalidateQueries({ queryKey: ["reports"] });
            onOpenChange(false);
            toast.success(editId ? "Relatório atualizado" : "Relatório enviado");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/reports/${id}`);
            qc.invalidateQueries({ queryKey: ["reports"] });
            toast.success("Relatório removido");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader
                overline="Rotina diária"
                title={isColaborador ? "Meus relatórios" : "Relatórios diários"}
                description={isColaborador ? "Registre a rotina do dia com fotos, observações e ajuda da voz." : "Acompanhe os relatórios enviados pela equipe."}
                action={isColaborador && (
                    <Button onClick={openNew} className="rounded-full px-6" data-testid="add-report-btn"><Plus className="mr-2 h-4 w-4" /> Novo relatório</Button>
                )}
            />

            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-lg">
                    <DialogHeader><DialogTitle className="font-heading text-2xl font-medium">{editId ? "Editar relatório" : "Relatório do dia"}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <VoiceInput formType="report" onResult={onVoiceResult} label="Ditar relatório" />
                        <div>
                            <Label>Data</Label>
                            <Input data-testid="report-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-xl" />
                        </div>
                        <div>
                            <Label>Rotina do dia</Label>
                            <Textarea data-testid="report-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1 rounded-xl" placeholder="Descreva as atividades realizadas..." />
                        </div>
                        <div>
                            <Label>Observações</Label>
                            <Textarea data-testid="report-observations" value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} rows={3} className="mt-1 rounded-xl" placeholder="Observações adicionais..." />
                        </div>
                        <div>
                            <Label>Fotos da rotina</Label>
                            <label data-testid="report-photo-upload" className="mt-1 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border bg-muted/40 p-6 text-center transition-colors hover:border-accent">
                                {uploading ? <Loader2 className="h-6 w-6 animate-spin text-accent" /> : <ImagePlus className="h-6 w-6 text-accent" />}
                                <span className="mt-2 text-sm text-muted-foreground">Toque para anexar fotos</span>
                                <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
                            </label>
                            {form.photos.length > 0 && (
                                <div className="mt-3 grid grid-cols-3 gap-2">
                                    {form.photos.map((p) => (
                                        <div key={p} className="relative">
                                            <AuthImage path={p} className="h-20 w-full rounded-xl object-cover" />
                                            <button onClick={() => removePhoto(p)} className="absolute -right-1.5 -top-1.5 rounded-full bg-expense p-1 text-white">
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <Button onClick={save} disabled={saving} className="w-full rounded-full" data-testid="save-report-btn">
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editId ? "Salvar alterações" : "Enviar relatório"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Filters q={filters.q} start={filters.start} end={filters.end} onChange={setFilters} placeholder="Buscar na rotina, observações ou colaborador..." />

            <div className="mt-6 space-y-4" data-testid="reports-list">
                {reports.length === 0 && <p className="text-sm text-muted-foreground">Nenhum relatório encontrado.</p>}
                {reports.map((r) => (
                    <div key={r.id} className="rounded-2xl border border-border bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] fade-up" data-testid={`report-card-${r.id}`}>
                        <div className="mb-3 flex items-start justify-between">
                            <div>
                                <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                                    <Calendar className="h-4 w-4" /> {formatDateBR(r.date)}
                                </div>
                                {!isColaborador && <p className="mt-1 text-sm text-muted-foreground">{r.user_name} · {r.nivel}</p>}
                            </div>
                            {canEdit && (
                                <div className="flex items-center gap-1">
                                    <button onClick={() => openEdit(r)} data-testid={`edit-report-${r.id}`} className="rounded-lg p-2 text-muted-foreground hover:bg-accent/10 hover:text-accent">
                                        <Pencil className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => remove(r.id)} data-testid={`delete-report-${r.id}`} className="rounded-lg p-2 text-muted-foreground hover:bg-expense/10 hover:text-expense">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{r.description}</p>
                        {r.observations && (
                            <div className="mt-3 flex gap-2 rounded-xl bg-muted/50 p-3 text-sm text-muted-foreground">
                                <StickyNote className="h-4 w-4 shrink-0" />
                                <p className="whitespace-pre-wrap">{r.observations}</p>
                            </div>
                        )}
                        {r.photos?.length > 0 && (
                            <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                                {r.photos.map((p) => (
                                    <AuthImage key={p} path={p} className="aspect-square w-full rounded-xl object-cover" />
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
